import {
  LayoutDashboard,
  CheckSquare,
  Calendar,
  FileText,
  StickyNote,
  Users,
  FolderKanban,
  UserCog,
  Wallet,
  GraduationCap,
  Command,
  BarChart3,
  Settings,
  CircleUser,
  IdCard,
  Banknote,
  Palmtree,
  Star,
  Target,
  ListChecks,
  Award,
  ReceiptText,
  Percent,
  Landmark,
  HandCoins,
  BookOpen,
  Library,
  Database,
  Bot,
  type LucideIcon,
} from 'lucide-react';

import type { Role } from '@/stores/session';

/** A single navigable link. */
export interface NavLeaf {
  label: string;
  to: string;
  roles?: Role[];
}

/** A collapsible group of links (a business module / area). */
export interface NavGroup {
  label: string;
  icon: LucideIcon;
  /** Base path used to auto-expand the group when a child route is active. */
  basePath: string;
  roles?: Role[];
  children: NavLeaf[];
}

/** A top-level standalone link. */
export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  roles?: Role[];
}

export type NavEntry = NavItem | NavGroup;

export interface NavSection {
  heading?: string;
  entries: NavEntry[];
}

export const isGroup = (e: NavEntry): e is NavGroup => 'children' in e;

export const BRAND_NAME = 'Dev13 Systems';

/**
 * Full navigation, mapped to docs/structure.md.
 * L0 platform surfaces live under Settings; Global Search (Command-K) and the
 * AI Assistant are header affordances, not sidebar entries.
 */
export const navSections: NavSection[] = [
  {
    // My Space — self-service cabinet, styled like Workspace (heading + icon
    // rows). No `roles` ⇒ visible to every staff role; data is self-scoped
    // server-side (apps/cabinet), so no role gate is needed here.
    heading: 'My Space',
    entries: [
      { label: 'Account', to: '/me/account', icon: CircleUser },
      { label: 'Employee Record', to: '/me/record', icon: IdCard },
      { label: 'My Salary', to: '/me/salary', icon: Banknote },
      { label: 'Invoices', to: '/me/invoices', icon: ReceiptText },
      // Personal earnings view — only the sales role earns commissions, so this
      // self-service entry is shown to Sales only (admins use Finance →
      // Commissions for the org-wide view).
      {
        label: 'Commissions',
        to: '/me/commissions',
        icon: Percent,
        roles: ['sales_manager'],
      },
      { label: 'Payslips', to: '/me/payslips', icon: HandCoins },
      { label: 'Bank Accounts', to: '/me/bank-accounts', icon: Landmark },
      { label: 'Time Off', to: '/me/time-off', icon: Palmtree },
      { label: 'Performance Reviews', to: '/me/reviews', icon: Star },
      { label: 'Development Plan', to: '/me/pdp', icon: Target },
      { label: 'Onboarding', to: '/me/onboarding', icon: ListChecks },
      { label: 'Skills', to: '/me/skills', icon: Award },
      { label: 'Documents', to: '/me/documents', icon: FileText },
    ],
  },
  {
    heading: 'Workspace',
    entries: [
      // The org-wide Dashboard is Founder / Super Admin only; everyone else
      // lands on My Space (see App.tsx `/`). Guests filtered wholesale.
      {
        label: 'Dashboard',
        to: '/',
        icon: LayoutDashboard,
        roles: ['super_admin', 'founder'],
      },
      { label: 'Tasks', to: '/tasks', icon: CheckSquare },
      { label: 'Calendar', to: '/calendar', icon: Calendar },
      {
        // Org-wide document library — Founder/Super Admin only. (Attaching
        // files to a specific employee/deal stays open to managers via that
        // entity's Attachments panel; this section is the whole-org browse.)
        // Employees see only their OWN documents under My Space → Documents.
        label: 'Documents',
        icon: FileText,
        basePath: '/documents',
        roles: ['super_admin', 'founder'],
        children: [
          { label: 'Files', to: '/documents/files' },
          { label: 'Template Library', to: '/documents/templates' },
        ],
      },
      { label: 'Notes', to: '/notes', icon: StickyNote },
    ],
  },
  {
    heading: 'Modules',
    entries: [
      {
        label: 'CRM',
        icon: Users,
        basePath: '/crm',
        roles: ['super_admin', 'founder', 'sales_manager', 'lead_gen'],
        children: [
          // Funnel reads Bids → Leads → Deals: the bid ledger sits in front of Leads.
          { label: 'Bids', to: '/crm/bids' },
          { label: 'Leads', to: '/crm/leads' },
          { label: 'Contacts', to: '/crm/contacts' },
          { label: 'Companies', to: '/crm/companies' },
          { label: 'Deals', to: '/crm/deals' },
          { label: 'Pipeline', to: '/crm/deals/board' },
          // Proposals & Contracts carry money — backend gates to money-visible roles (C7).
          { label: 'Proposals', to: '/crm/proposals', roles: ['super_admin', 'founder', 'sales_manager', 'accountant'] },
          { label: 'Contracts', to: '/crm/contracts', roles: ['super_admin', 'founder', 'sales_manager', 'accountant'] },
          { label: 'Activities', to: '/crm/activities' },
          // The org's ideal customer profiles (ADR-108); Lead Gen reads, managers edit.
          { label: 'ICP Segments', to: '/crm/icp-segments' },
        ],
      },
      {
        label: 'Projects',
        icon: FolderKanban,
        basePath: '/projects',
        children: [
          // Operational — every staff member needs these (see/deliver their work,
          // log time). Backend grants the Employee role project/milestone view + time entry.
          { label: 'Projects', to: '/projects' },
          { label: 'Worklogs', to: '/projects/worklogs' },
          // Management / financial — capacity planning, cost, reporting, client-facing.
          // Restricted to admins (+ Accountant for the money views). UX gate only —
          // the project endpoints are staff-open server-side (F2).
          { label: 'Resource Allocation', to: '/projects/resources', roles: ['super_admin', 'founder'] },
          { label: 'Budget vs Actual', to: '/projects/budget', roles: ['super_admin', 'founder', 'accountant'] },
          { label: 'Reports', to: '/projects/reports', roles: ['super_admin', 'founder', 'accountant'] },
        ],
      },
      {
        label: 'HRM',
        icon: UserCog,
        basePath: '/hrm',
        roles: ['super_admin', 'founder', 'hr'],
        children: [
          { label: 'Recruitment', to: '/hrm/recruitment' },
          { label: 'Employees', to: '/hrm/employees' },
          { label: 'Time Off', to: '/hrm/time-off' },
          { label: 'Onboarding', to: '/hrm/onboarding' },
          { label: 'Performance Reviews', to: '/hrm/reviews' },
          { label: 'PDP', to: '/hrm/pdp' },
          { label: 'Skills Matrix', to: '/hrm/skills' },
          { label: 'Departments', to: '/hrm/departments' },
          { label: 'Org Chart', to: '/hrm/org-chart' },
        ],
      },
      {
        label: 'Finance',
        icon: Wallet,
        basePath: '/finance',
        roles: ['super_admin', 'founder', 'accountant'],
        children: [
          { label: 'Service Catalog', to: '/finance/catalog' },
          { label: 'Billing', to: '/finance/billing' },
          { label: 'Staff Invoices', to: '/finance/staff-invoices' },
          {
            label: 'Commissions',
            to: '/finance/commissions',
            roles: ['super_admin', 'founder', 'accountant'],
          },
          {
            label: 'Recurring Expenses',
            to: '/finance/recurring-expenses',
            roles: ['super_admin', 'founder', 'accountant'],
          },
          { label: 'Income', to: '/finance/income' },
          { label: 'Expenses', to: '/finance/expenses' },
          { label: 'Payroll', to: '/finance/payroll' },
          { label: 'Taxes', to: '/finance/taxes' },
          { label: 'Budgeting', to: '/finance/budgeting' },
          { label: 'Reports', to: '/finance/reports' },
        ],
      },
      {
        label: 'LMS',
        icon: GraduationCap,
        basePath: '/lms',
        children: [
          { label: 'Courses', to: '/lms/courses' },
          { label: 'Materials', to: '/lms/materials' },
          { label: 'Guides', to: '/lms/guides' },
          { label: 'Education Zone', to: '/lms/education' },
          { label: 'Assessments', to: '/lms/assessments' },
        ],
      },
      {
        // Installed AI agents directory + results — visible to all staff (Guest excluded).
        label: 'AI Team',
        icon: Bot,
        basePath: '/ai-team',
        children: [
          { label: 'Directory', to: '/ai-team' },
          { label: 'Results', to: '/ai-team/results' },
        ],
      },
      {
        label: 'Command Center',
        icon: Command,
        basePath: '/command-center',
        roles: ['super_admin', 'founder'],
        children: [
          { label: 'Executive Dashboard', to: '/command-center' },
          { label: 'Upwork Analytics', to: '/command-center/upwork' },
          { label: 'LinkedIn Analytics', to: '/command-center/linkedin' },
          { label: 'Website Analytics', to: '/command-center/website' },
          { label: 'Market Trends', to: '/command-center/market' },
          { label: 'News Feed', to: '/command-center/news' },
        ],
      },
    ],
  },
  {
    heading: 'Insights',
    entries: [
      {
        label: 'Analytics',
        icon: BarChart3,
        basePath: '/analytics',
        roles: ['super_admin', 'founder', 'accountant'],
        children: [
          { label: 'Dashboards', to: '/analytics' },
          { label: 'KPIs & Goals', to: '/analytics/kpis' },
          { label: 'Exports', to: '/analytics/exports' },
        ],
      },
    ],
  },
  {
    heading: 'Admin',
    entries: [
      {
        label: 'Settings',
        icon: Settings,
        basePath: '/settings',
        roles: ['super_admin', 'founder'],
        children: [
          { label: 'Organization', to: '/settings/organization' },
          { label: 'Members & Roles', to: '/settings/members' },
          { label: 'Integrations', to: '/settings/integrations' },
          // Inbox now lives in the header bell (NotificationBell → "View all" still
          // opens /settings/notifications). Only Preferences stays in the nav.
          { label: 'Notification Preferences', to: '/settings/notifications/preferences' },
          { label: 'Custom Fields', to: '/settings/custom-fields' },
          { label: 'Project Defaults', to: '/settings/project-defaults' },
          { label: 'API Keys & Bots', to: '/settings/api-keys' },
          { label: 'Agents', to: '/settings/agents' },
          { label: 'Audit Log', to: '/settings/audit' },
        ],
      },
    ],
  },
  {
    // Standalone bottom hub — both cross-cutting, visible to all staff (Guest
    // excluded). Wiki = knowledge base (ADR-075), independent of project Pages.
    entries: [
      { label: 'Wiki', to: '/wiki', icon: Library },
      // Org-wide RAG corpora — visible to all staff (Guest excluded).
      { label: 'Knowledge Base', to: '/knowledge-base', icon: Database },
      { label: 'Help & Guides', to: '/guides', icon: BookOpen },
    ],
  },
];

export const visibleForRole = (roles: Role[] | undefined, role: Role): boolean =>
  !roles || roles.includes(role);

/**
 * Filter the nav for the active role (F2). Guests (clients) are project members
 * with a minimal nav — just Projects (scoped to their memberships server-side).
 * Everything else stays staff-only (deny-by-default).
 */
export function navSectionsForRole(role: Role | null): NavSection[] {
  if (!role) return [];
  if (role === 'guest') {
    return [
      {
        heading: 'Workspace',
        entries: [
          { label: 'Projects', to: '/projects', icon: FolderKanban },
          { label: 'Help', to: '/guides', icon: BookOpen },
        ],
      },
    ];
  }
  return navSections
    .map((section) => ({
      ...section,
      entries: section.entries
        .filter((e) => visibleForRole(e.roles, role))
        // Also filter group children by their own roles (e.g. Proposals).
        .map((e) =>
          isGroup(e) ? { ...e, children: e.children.filter((c) => visibleForRole(c.roles, role)) } : e,
        )
        .filter((e) => !isGroup(e) || e.children.length > 0),
    }))
    .filter((section) => section.entries.length > 0);
}

/** Flatten every routable path (group children + standalone items) for the router. */
export function allNavRoutes(): { path: string; title: string }[] {
  const routes: { path: string; title: string }[] = [];
  for (const section of navSections) {
    for (const entry of section.entries) {
      if (isGroup(entry)) {
        for (const child of entry.children) routes.push({ path: child.to, title: child.label });
      } else {
        routes.push({ path: entry.to, title: entry.label });
      }
    }
  }
  // De-dupe (e.g. /projects and /command-center appear as both base + first child).
  const seen = new Set<string>();
  return routes.filter((r) => (seen.has(r.path) ? false : seen.add(r.path)));
}
